class LuneraTheme {
  constructor() {
    this.body = document.body;
    this.drawer = document.querySelector('#CartDrawer');
    this.header = document.querySelector('[data-site-header]');
    this.lastScroll = 0;
    this.moneyFormat = window.Shopify?.currency?.active === 'TRY' ? '{{amount_with_comma_separator}} ₺' : '{{amount}}';
    this.bind();
  }

  bind() {
    this.initHeader();
    this.initHero();
    this.initReveal();
    this.initPanels();
    this.initCart();
    this.initHotspots();
    this.initProducts();
    this.initProductContent();
    this.initCarousels();
    this.initCollection();
  }

  initHero() {
    document.querySelectorAll('[data-hero-slider]').forEach(slider => {
      const slides = [...slider.querySelectorAll('[data-hero-slide]')];
      const dots = [...slider.querySelectorAll('[data-hero-dot]')];
      if (slides.length < 2) return;
      let index = 0, timer;
      const speed = Number(slider.dataset.speed) || 6000;
      slider.style.setProperty('--hero-speed', `${speed}ms`);
      const show = next => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => { dot.classList.remove('is-active'); if (i === index) requestAnimationFrame(() => dot.classList.add('is-active')); });
      };
      const play = () => { clearInterval(timer); if (slider.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(() => show(index + 1), speed); };
      dots.forEach(dot => dot.addEventListener('click', () => { show(Number(dot.dataset.heroDot)); play(); }));
      slider.querySelector('[data-hero-prev]')?.addEventListener('click', () => { show(index - 1); play(); });
      slider.querySelector('[data-hero-next]')?.addEventListener('click', () => { show(index + 1); play(); });
      slider.addEventListener('mouseenter', () => clearInterval(timer)); slider.addEventListener('mouseleave', play);
      play();
    });
  }

  initHeader() {
    if (!this.header) return;
    const onScroll = () => {
      const y = window.scrollY;
      this.header.classList.toggle('is-sticky', this.header.dataset.sticky !== 'false' && y > 45);
      this.header.classList.remove('is-hidden');
      this.lastScroll = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  initReveal() {
    const items = document.querySelectorAll('.reveal');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) return items.forEach(x => x.classList.add('is-visible'));
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }), { threshold: .08, rootMargin: '0px 0px -35px' });
    items.forEach((item, i) => { item.style.transitionDelay = `${Math.min(i % 4, 3) * 70}ms`; observer.observe(item); });
  }

  togglePanel(panel, open) {
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    this.body.classList.toggle('is-locked', open);
  }

  initPanels() {
    document.querySelectorAll('[data-cart-open]').forEach(btn => btn.addEventListener('click', () => this.togglePanel(this.drawer, true)));
    document.querySelectorAll('[data-drawer-close]').forEach(btn => btn.addEventListener('click', () => this.togglePanel(this.drawer, false)));
    const search = document.querySelector('[data-search-panel]');
    document.querySelector('[data-search-open]')?.addEventListener('click', () => { this.togglePanel(search, true); setTimeout(() => search.querySelector('input')?.focus(), 350); });
    document.querySelector('[data-search-close]')?.addEventListener('click', () => this.togglePanel(search, false));
    const mobile = document.querySelector('[data-mobile-panel]');
    document.querySelector('[data-mobile-menu]')?.addEventListener('click', e => {
      const open = !mobile.classList.contains('is-open');
      mobile.classList.toggle('is-open', open); this.body.classList.toggle('is-locked', open); e.currentTarget.setAttribute('aria-expanded', String(open));
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') { this.togglePanel(this.drawer, false); this.togglePanel(search, false); } });
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency: window.Shopify?.currency?.active || 'TRY' }).format(cents / 100);
  }

  async cartRequest(url, body) {
    const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  initCart() {
    document.addEventListener('click', async e => {
      const quick = e.target.closest('[data-quick-add]');
      if (quick) {
        e.preventDefault(); quick.disabled = true;
        const label = quick.querySelector('span'); const original = label?.textContent;
        try { await this.cartRequest('/cart/add.js', { items: [{ id: Number(quick.dataset.quickAdd), quantity: 1 }] }); await this.refreshCart(); if (label) label.textContent = 'Eklendi'; this.togglePanel(this.drawer, true); }
        catch { if (label) label.textContent = 'Tekrar deneyin'; }
        finally { setTimeout(() => { quick.disabled = false; if (label) label.textContent = original; }, 900); }
      }
      const qty = e.target.closest('[data-qty]');
      if (qty) { const item = qty.closest('[data-line]'); const current = Number(item.querySelector('.qty span').textContent); await this.cartRequest('/cart/change.js', { line: Number(item.dataset.line), quantity: Math.max(0, current + Number(qty.dataset.qty)) }); await this.refreshCart(); }
    });
    this.refreshCart().catch(() => {});
  }

  async refreshCart() {
    const cart = await fetch('/cart.js').then(r => r.json());
    document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
    document.querySelector('[data-cart-total]')?.replaceChildren(document.createTextNode(this.formatMoney(cart.total_price)));
    const box = document.querySelector('[data-cart-items]');
    if (box) box.innerHTML = cart.items.length ? cart.items.map((item, i) => `<div class="cart-item" data-line="${i + 1}"><a href="${item.url}"><img src="${item.image || ''}" alt=""></a><div><p class="eyebrow">${item.vendor || ''}</p><a href="${item.url}">${item.product_title}</a><small>${item.variant_title || ''}</small><div class="qty"><button data-qty="-1">−</button><span>${item.quantity}</span><button data-qty="1">+</button></div></div><strong>${this.formatMoney(item.final_line_price)}</strong></div>`).join('') : '<p class="drawer__empty">Sepetiniz şu an boş.</p>';
    const meter = document.querySelector('.shipping-meter');
    if (meter) {
      const threshold = Number(meter.dataset.threshold); const remaining = Math.max(0, threshold - cart.total_price); const percent = threshold ? Math.min(100, cart.total_price / threshold * 100) : 100;
      meter.querySelector('[data-shipping-copy]').textContent = remaining ? `Ücretsiz kargo için ${this.formatMoney(remaining)} kaldı` : 'Ücretsiz kargoyu kazandınız';
      meter.querySelector('[data-shipping-progress]').style.width = `${percent}%`;
    }
  }

  initHotspots() {
    document.querySelectorAll('[data-hotspot]').forEach(hotspot => hotspot.addEventListener('click', () => {
      const scope = hotspot.closest('.lookbook'); const index = hotspot.dataset.hotspot;
      scope.querySelectorAll('[data-hotspot], [data-look-product]').forEach(el => el.classList.remove('is-active'));
      hotspot.classList.add('is-active'); scope.querySelector(`[data-look-product="${index}"]`)?.classList.add('is-active');
    }));
  }

  initProducts() {
    document.querySelectorAll('[data-product-qty]').forEach(btn => btn.addEventListener('click', () => { const input = btn.parentElement.querySelector('input'); input.value = Math.max(1, Number(input.value) + Number(btn.dataset.productQty)); }));
    document.querySelectorAll('[data-product-root]').forEach(root => {
      const form = root.querySelector('[data-product-form]');
      const json = root.querySelector('[data-product-json]');
      const variants = json ? JSON.parse(json.textContent) : [];
      const syncVariant = () => {
        if (!form || !variants.length) return;
        const options = [...form.querySelectorAll('.product-option')].map(fieldset => fieldset.querySelector('input:checked')?.value);
        const variant = variants.find(item => item.options.every((value, index) => value === options[index]));
        if (!variant) return;
        form.querySelector('[data-variant-id]').value = variant.id;
        const mode = root.dataset.productMode;
        root.querySelectorAll('[data-product-price] span:first-child, [data-add-price]').forEach(el => { el.textContent = el.matches('[data-add-price]') && mode === 'coming-soon' ? '' : `${el.matches('[data-add-price]') ? '· ' : ''}${this.formatMoney(variant.price)}`; });
        const add = form.querySelector('[name="add"]');
        add.disabled = !variant.available || mode === 'coming-soon';
        form.querySelector('[data-add-label]').textContent = mode === 'coming-soon' ? 'Yakında' : mode === 'pre-order' ? 'Ön sipariş ver' : variant.available ? 'Sepete ekle' : 'Tükendi';
        form.querySelectorAll('.product-option').forEach(fieldset => { const selected = fieldset.querySelector('input:checked'); fieldset.querySelector('[data-option-value]').textContent = selected?.value || ''; });
        history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      };
      form?.querySelectorAll('.product-option input').forEach(input => input.addEventListener('change', syncVariant));
      form?.addEventListener('submit', async e => { if (e.submitter?.name !== 'add') return; e.preventDefault(); const button = form.querySelector('[name="add"]'); const label = form.querySelector('[data-add-label]'); const original = label?.textContent; button.disabled = true; try { const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) }); if (!response.ok) throw new Error('Product could not be added'); await this.refreshCart(); if (label) label.textContent = 'Sepete eklendi'; this.togglePanel(this.drawer, true); } catch { if (label) label.textContent = 'Tekrar deneyin'; } finally { setTimeout(() => { button.disabled = false; if (label) label.textContent = original; }, 900); } });
      const gallery = root.querySelector('[data-product-gallery]');
      if (gallery) gallery.addEventListener('scroll', () => { const width = gallery.firstElementChild?.getBoundingClientRect().width || 1; const index = Math.round(gallery.scrollLeft / width); const counter = root.querySelector('[data-media-current]'); if (counter) counter.textContent = index + 1; root.querySelectorAll('[data-media-thumb]').forEach((thumb, i) => thumb.classList.toggle('is-active', i === index)); root.querySelectorAll('[data-media-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === index)); }, { passive: true });
      root.querySelectorAll('[data-media-thumb]').forEach((thumb, index) => thumb.addEventListener('click', event => {
        if (matchMedia('(max-width: 989px)').matches) return;
        event.preventDefault();
        root.querySelectorAll('[data-media-thumb]').forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
        root.querySelectorAll('[data-media-index]').forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
        const counter = root.querySelector('[data-media-current]');
        if (counter) counter.textContent = index + 1;
      }));
      root.querySelectorAll('[data-media-dot]').forEach((dot, index) => dot.addEventListener('click', () => gallery?.scrollTo({ left: (gallery.firstElementChild?.getBoundingClientRect().width || 0) * index, behavior: 'smooth' })));
    });
  }

  initProductContent() {
    document.querySelectorAll('[data-product-content]').forEach(section => {
      const tabs = [...section.querySelectorAll('[data-content-tab]')];
      const panes = [...section.querySelectorAll('[data-content-pane]')];
      tabs.forEach(tab => tab.addEventListener('click', () => {
        const target = tab.dataset.contentTab;
        tabs.forEach(item => { const active = item === tab; item.classList.toggle('is-active', active); item.setAttribute('aria-selected', String(active)); });
        panes.forEach(pane => { const active = pane.dataset.contentPane === target; pane.classList.toggle('is-active', active); pane.hidden = !active; });
      }));
    });
  }

  initCarousels() {
    document.querySelectorAll('[data-carousel-section]').forEach(section => {
      const scroller = section.querySelector('[data-drag-scroll]');
      if (!scroller) return;
      const distance = () => Math.max(280, scroller.clientWidth * .78);
      section.querySelector('[data-carousel-prev]')?.addEventListener('click', () => scroller.scrollBy({ left: -distance(), behavior: 'smooth' }));
      section.querySelector('[data-carousel-next]')?.addEventListener('click', () => scroller.scrollBy({ left: distance(), behavior: 'smooth' }));
      let active = false, startX = 0, startScroll = 0, dragged = false;
      scroller.addEventListener('pointerdown', e => { active = true; dragged = false; startX = e.clientX; startScroll = scroller.scrollLeft; scroller.setPointerCapture?.(e.pointerId); scroller.classList.add('is-dragging'); });
      scroller.addEventListener('pointermove', e => { if (!active) return; const delta = e.clientX - startX; if (Math.abs(delta) > 5) dragged = true; scroller.scrollLeft = startScroll - delta; });
      const stop = () => { active = false; scroller.classList.remove('is-dragging'); };
      scroller.addEventListener('pointerup', stop); scroller.addEventListener('pointercancel', stop);
      scroller.addEventListener('click', e => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } }, true);
      scroller.addEventListener('keydown', e => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); scroller.scrollBy({ left: e.key === 'ArrowRight' ? distance() : -distance(), behavior: 'smooth' }); } });
    });
  }

  initCollection() {
    document.querySelector('[data-sort]')?.addEventListener('change', e => { const url = new URL(location.href); url.searchParams.set('sort_by', e.target.value); location.href = url; });
    const filters = document.querySelector('[data-filter-drawer]');
    const toggleFilters = open => { if (!filters) return; filters.classList.toggle('is-open', open); filters.setAttribute('aria-hidden', String(!open)); this.body.classList.toggle('is-locked', open); };
    document.querySelector('[data-filter-open]')?.addEventListener('click', () => toggleFilters(true));
    document.querySelectorAll('[data-filter-close]').forEach(button => button.addEventListener('click', () => toggleFilters(false)));
  }
}

document.addEventListener('DOMContentLoaded', () => new LuneraTheme());
